// HazardsViewPanel - Browse all reported road hazards
// Shows hazards with filtering, search, and navigation

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  LocationOn as LocationOnIcon,
  Warning as WarningIcon,
  LocalPolice as LocalPoliceIcon,
  CarCrash as CarCrashIcon,
  Whatshot as WhatshotIcon,
  Block as BlockIcon,
  Map as MapIcon,
  Share as ShareIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  AddAPhoto as AddAPhotoIcon,
} from '@mui/icons-material';
import { telegram } from '../services/telegram';
import { getCyberColors } from '../theme/cyberColors';
import { useAppStore } from '../stores/appStore';
import { RoadHazard } from '../services/roadHazards';

interface HazardsViewPanelProps {
  open: boolean;
  onClose: () => void;
  hazards: RoadHazard[];
  onNavigate?: (lat: number, lng: number, name: string) => void;
  onVote?: (hazardId: string, vote: number) => void;
  onAddPhoto?: (hazard: { id: string; name: string; type: string; latitude: number; longitude: number }) => void;
}

const HAZARD_TYPE_INFO: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  cop: { icon: <LocalPoliceIcon />, label: 'Police', color: '#3b82f6' },
  accident: { icon: <CarCrashIcon />, label: 'Accident', color: '#ef4444' },
  hazard: { icon: <WarningIcon />, label: 'Hazard', color: '#f59e0b' },
  roadClosed: { icon: <BlockIcon />, label: 'Road Closed', color: '#dc2626' },
  fire: { icon: <WhatshotIcon />, label: 'Fire', color: '#f97316' },
  other: { icon: <WarningIcon />, label: 'Other', color: '#94a3b8' },
};

export function HazardsViewPanel({ open, onClose, hazards, onNavigate, onVote, onAddPhoto }: HazardsViewPanelProps) {
  const darkMode = useAppStore((state) => state.darkMode);
  const highContrast = useAppStore((state) => state.highContrast);
  const colors = getCyberColors(darkMode, highContrast);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  // Filter hazards
  const filteredHazards = hazards.filter((hazard) => {
    // Type filter
    if (filterType !== 'all' && hazard.type !== filterType) {
      return false;
    }

    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        hazard.type.toLowerCase().includes(query) ||
        hazard.note?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  const getHazardInfo = (type: string) => {
    return HAZARD_TYPE_INFO[type] || HAZARD_TYPE_INFO['other'];
  };

  const formatTimeAgo = (createdAt: string) => {
    const timestamp = new Date(createdAt).getTime();
    const now = Date.now();
    const diffMs = now - timestamp;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: darkMode ? 'rgba(15, 23, 42, 0.98)' : 'rgba(255, 255, 255, 0.98)',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        backdropFilter: 'blur(10px)',
      }}
    >
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 2,
          borderRadius: 0,
          bgcolor: darkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(248, 250, 252, 0.95)',
          borderBottom: `2px solid ${colors.neonPink}50`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: colors.neonPink, fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? '#fff' : '#000' }}>
              Road Hazards
            </Typography>
            <Chip
              label={filteredHazards.length}
              size="small"
              sx={{
                bgcolor: colors.neonPink,
                color: '#fff',
                fontWeight: 700,
              }}
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Search and Filter */}
        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search hazards..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.neonPink }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: darkMode ? 'rgba(15, 23, 42, 0.5)' : 'rgba(255, 255, 255, 0.9)',
                '& fieldset': { borderColor: `${colors.neonPink}30` },
                '&:hover fieldset': { borderColor: `${colors.neonPink}60` },
                '&.Mui-focused fieldset': { borderColor: colors.neonPink },
              },
            }}
          />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="All"
              onClick={() => setFilterType('all')}
              color={filterType === 'all' ? 'primary' : 'default'}
              size="small"
            />
            {Object.entries(HAZARD_TYPE_INFO).map(([type, info]) => (
              <Chip
                key={type}
                icon={info.icon as any}
                label={info.label}
                onClick={() => setFilterType(type)}
                color={filterType === type ? 'primary' : 'default'}
                size="small"
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {filteredHazards.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {searchQuery || filterType !== 'all'
              ? 'No hazards match your search'
              : 'No hazards reported yet'}
          </Alert>
        ) : (
          <Stack spacing={2}>
            {filteredHazards.map((hazard) => {
              const hazardInfo = getHazardInfo(hazard.type);
              
              return (
                <Card
                  key={hazard.id}
                  sx={{
                    background: colors.gradientCard,
                    border: `2px solid ${colors.borderDefault}`,
                    borderRadius: 3,
                    boxShadow: colors.shadowCard,
                    backdropFilter: 'blur(12px)',
                    '&:hover': {
                      borderColor: colors.neonPink,
                      boxShadow: `${colors.shadowElevated}, 0 0 30px ${colors.neonPink}30`,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s ease',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    {/* Header: Icon + Type + Time */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          bgcolor: hazardInfo.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          color: '#fff',
                          border: `3px solid ${hazardInfo.color}40`,
                          boxShadow: `0 0 20px ${hazardInfo.color}60, inset 0 0 10px ${hazardInfo.color}40`,
                        }}
                      >
                        {hazardInfo.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: darkMode ? '#fff' : '#000',
                          }}
                        >
                          {hazardInfo.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: darkMode ? '#94a3b8' : '#64748b' }}>
                          {formatTimeAgo(hazard.created_at)}
                        </Typography>
                      </Box>
                      {/* Votes */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.neonCyan }}>
                          {hazard.votes || 0}
                        </Typography>
                        <ThumbUpIcon sx={{ fontSize: 16, color: colors.neonCyan }} />
                      </Box>
                    </Box>

                    {/* Note/Description */}
                    {hazard.note && (
                      <Typography variant="body2" sx={{ color: darkMode ? '#cbd5e1' : '#475569', mb: 1.5 }}>
                        {hazard.note}
                      </Typography>
                    )}

                    {/* Info Chips Row */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      {/* Coordinates Chip */}
                      <Chip
                        icon={<LocationOnIcon sx={{ fontSize: 14 }} />}
                        label={`${hazard.latitude.toFixed(4)}, ${hazard.longitude.toFixed(4)}`}
                        size="small"
                        sx={{
                          height: 24,
                          bgcolor: darkMode ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
                          color: darkMode ? '#fca5a5' : '#991b1b',
                          fontSize: 12,
                          '& .MuiChip-icon': { color: darkMode ? '#fca5a5' : '#991b1b' },
                        }}
                      />
                      {/* Reporter */}
                      <Chip
                        label={hazard.reporter_name || 'Anonymous'}
                        size="small"
                        sx={{
                          height: 24,
                          bgcolor: darkMode ? 'rgba(148, 163, 184, 0.2)' : '#f1f5f9',
                          color: darkMode ? '#cbd5e1' : '#475569',
                          fontSize: 12,
                        }}
                      />
                    </Box>

                    {/* Action Buttons Row */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                      {/* Upvote */}
                      <IconButton
                        size="small"
                        onClick={() => onVote && onVote(hazard.id, 1)}
                        sx={{
                          width: 40,
                          height: 40,
                          border: `2px solid ${colors.borderDefault}`,
                          borderRadius: 2,
                          bgcolor: `${colors.neonCyan}10`,
                          '&:hover': { 
                            bgcolor: `${colors.neonCyan}20`,
                            borderColor: colors.neonCyan,
                            boxShadow: `0 0 15px ${colors.neonCyan}40`,
                          },
                        }}
                      >
                        <ThumbUpIcon sx={{ fontSize: 18, color: darkMode ? '#94a3b8' : '#64748b' }} />
                      </IconButton>
                      {/* Downvote */}
                      <IconButton
                        size="small"
                        onClick={() => onVote && onVote(hazard.id, -1)}
                        sx={{
                          width: 40,
                          height: 40,
                          border: `2px solid ${colors.borderDefault}`,
                          borderRadius: 2,
                          bgcolor: `${colors.neonRed}10`,
                          '&:hover': { 
                            bgcolor: `${colors.neonRed}20`,
                            borderColor: colors.neonRed,
                            boxShadow: `0 0 15px ${colors.neonRed}40`,
                          },
                        }}
                      >
                        <ThumbDownIcon sx={{ fontSize: 18, color: darkMode ? '#94a3b8' : '#64748b' }} />
                      </IconButton>
                      {/* Share */}
                      <IconButton
                        size="small"
                        sx={{
                          width: 40,
                          height: 40,
                          border: `2px solid ${colors.borderDefault}`,
                          borderRadius: 2,
                          bgcolor: `${colors.neonPurple}10`,
                          '&:hover': { 
                            bgcolor: `${colors.neonPurple}20`,
                            borderColor: colors.neonPurple,
                            boxShadow: `0 0 15px ${colors.neonPurple}40`,
                          },
                        }}
                      >
                        <ShareIcon sx={{ fontSize: 18, color: darkMode ? '#94a3b8' : '#64748b' }} />
                      </IconButton>
                      {/* Add Photo Evidence */}
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (onAddPhoto) {
                            onAddPhoto({
                              id: hazard.id,
                              name: `${hazardInfo.label} Hazard`,
                              type: 'hazard',
                              latitude: hazard.latitude,
                              longitude: hazard.longitude
                            });
                            telegram.hapticFeedback('light');
                          }
                        }}
                        disabled={!onAddPhoto}
                        sx={{
                          width: 40,
                          height: 40,
                          border: `2px solid ${onAddPhoto ? '#a21caf' : colors.borderDefault}`,
                          borderRadius: 2,
                          bgcolor: onAddPhoto ? 'rgba(162, 28, 175, 0.15)' : `${colors.borderDefault}10`,
                          opacity: onAddPhoto ? 1 : 0.5,
                          '&:hover': { 
                            bgcolor: 'rgba(162, 28, 175, 0.25)',
                            borderColor: '#a21caf',
                            boxShadow: '0 0 15px rgba(162, 28, 175, 0.4)',
                          },
                        }}
                      >
                        <AddAPhotoIcon sx={{ fontSize: 18, color: onAddPhoto ? '#a21caf' : (darkMode ? '#94a3b8' : '#64748b') }} />
                      </IconButton>
                      {/* Google Maps */}
                      <IconButton
                        size="small"
                        onClick={() => {
                          const url = `https://www.google.com/maps/dir/?api=1&destination=${hazard.latitude},${hazard.longitude}`;
                          window.open(url, '_blank');
                          telegram.hapticFeedback('light');
                        }}
                        sx={{
                          width: 40,
                          height: 40,
                          border: `2px solid #4285F4`,
                          borderRadius: 2,
                          bgcolor: '#4285F4',
                          color: '#fff',
                          boxShadow: '0 0 15px rgba(66, 133, 244, 0.4)',
                          '&:hover': { 
                            bgcolor: '#357ae8',
                            boxShadow: '0 0 25px rgba(66, 133, 244, 0.6)',
                            transform: 'scale(1.05)',
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>G</Typography>
                      </IconButton>
                      {/* Apple Maps */}
                      <IconButton
                        size="small"
                        onClick={() => {
                          const url = `http://maps.apple.com/?daddr=${hazard.latitude},${hazard.longitude}`;
                          window.open(url, '_blank');
                          telegram.hapticFeedback('light');
                        }}
                        sx={{
                          width: 40,
                          height: 40,
                          border: `2px solid #333`,
                          borderRadius: 2,
                          bgcolor: '#000',
                          color: '#fff',
                          boxShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
                          '&:hover': { 
                            bgcolor: '#333',
                            boxShadow: '0 0 25px rgba(0, 0, 0, 0.7)',
                            transform: 'scale(1.05)',
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>A</Typography>
                      </IconButton>
                      {/* Waze */}
                      <IconButton
                        size="small"
                        onClick={() => {
                          const url = `https://www.waze.com/ul?ll=${hazard.latitude},${hazard.longitude}&navigate=yes`;
                          window.open(url, '_blank');
                          telegram.hapticFeedback('light');
                        }}
                        sx={{
                          width: 40,
                          height: 40,
                          border: `2px solid #33CCFF`,
                          borderRadius: 2,
                          bgcolor: '#33CCFF',
                          color: '#fff',
                          boxShadow: '0 0 15px rgba(51, 204, 255, 0.4)',
                          '&:hover': { 
                            bgcolor: '#00b8e6',
                            boxShadow: '0 0 25px rgba(51, 204, 255, 0.6)',
                            transform: 'scale(1.05)',
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>W</Typography>
                      </IconButton>
                      {/* In-App Map */}
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (onNavigate) {
                            onNavigate(hazard.latitude, hazard.longitude, `${hazardInfo.label} Hazard`);
                            onClose();
                          }
                          telegram.hapticFeedback('medium');
                        }}
                        sx={{
                          width: 40,
                          height: 40,
                          border: `2px solid ${colors.neonRed}`,
                          borderRadius: 2,
                          bgcolor: colors.neonRed,
                          color: '#fff',
                          boxShadow: `0 0 20px ${colors.neonRed}60`,
                          '&:hover': { 
                            bgcolor: '#b91c1c',
                            boxShadow: `0 0 30px ${colors.neonRed}80`,
                            transform: 'scale(1.05)',
                          },
                        }}
                      >
                        <MapIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
